import * as React from "react";
import "../filters.scss";
import {default as OptionSelector} from "../../../../hiberUiKit/src/molecules/OptionSelector/OptionSelector";
import RadioButton, {IRadioButton} from "../../../../hiberUiKit/src/atoms/Buttons/RadioButton";
import {none, option, Option} from "ts-option";
import {DateFilterType, DateFilterTypeName, unitsOfTime} from "./DateFilter";
import DateFilterModel from "../../../../model/workspace/DateFilterModel";
import {DateFilterValueItemApiDto} from "../../../../api/model/rest";
import NumberUtils from "../../../../utils/NumberUtils";
import moment = require("moment");
import DurationConstructor = moment.unitOfTime.DurationConstructor;

interface IProps {
	/**
	 * Input value
	 */
	value: Option<MoreThanFromNowFilterModel>
	/**
	 * On change callback
	 * @param {Option<MoreThanFromNowFilterModel>} value
	 */
	onChange: (value: Option<MoreThanFromNowFilterModel>) => void
	/**
	 * Selected radio button Id
	 */
	selectedRadioButtonId: string
	/**
	 * On select button
	 * @param {string} id
	 */
	onSelectButton: (id: string) => void
}

interface IState {
	/**
	 * Selected unit of time for "More than from now"
	 */
	selectedUnitOfTime: DurationConstructor
	/**
	 * Represents input value for "More than from now" (start date in future)
	 * Result time span is represented by everything after the specified start date in future
	 */
	inputStartValue: string
}

/**
 * Component that allows filtering with use of date from future
 * Includes everything after the specified time
 */
export default class MoreThanFromNowFilter extends React.Component<IProps, IState> {

	radioButton: IRadioButton = {
		label: DateFilterTypeName[DateFilterType.MoreThanFromNow],
		disabled: false,
		value: DateFilterType.MoreThanFromNow
	};

	constructor(props: IProps) {
		super(props);
		this.state = {
			inputStartValue: props.value.map(v => v.timeValue.toString()).getOrElse(""),
			selectedUnitOfTime: props.value.map(v => v.unitOfTime).getOrElse("minutes"),
		};
		this.onChangeInput = this.onChangeInput.bind(this);
		this.onChangeSelector = this.onChangeSelector.bind(this);
		this.onChangeCallback = this.onChangeCallback.bind(this);
		this.resetFilter = this.resetFilter.bind(this);
	}

	componentWillReceiveProps(nextProps: IProps) {
		if (this.props.selectedRadioButtonId === this.radioButton.value &&
			nextProps.selectedRadioButtonId !== this.radioButton.value) {
			this.resetFilter();
		}
	}

	render() {
		const radioButtonChecked = (this.props.selectedRadioButtonId === this.radioButton.value);
		return (
			<div className={"c-filter-dates_inputs_row"}>
				<RadioButton radioButton={this.radioButton}
							 onChange={() => this.props.onSelectButton(this.radioButton.value)}
							 checked={radioButtonChecked}/>
				<input className={"c-filter-dates_input_more-than"}
					   type="number"
					   value={this.state.inputStartValue}
					   onChange={this.onChangeInput}
					   disabled={!radioButtonChecked}/>
				<OptionSelector selectableOptions={unitsOfTime}
								onSelect={this.onChangeSelector}
								disabled={!radioButtonChecked}
								selectedOption={this.state.selectedUnitOfTime}/>
				<label className={"t-body c-filter-dates_inputs_row_label"}>from now</label>
			</div>
		);
	}

	/**
	 * On change in "More than from now" input
	 */
	onChangeInput(e: React.FormEvent<HTMLInputElement>) {
		if (e.currentTarget.value.length == 0 || NumberUtils.isNumber(e.currentTarget.value)) {
			this.setState({inputStartValue: e.currentTarget.value}, this.onChangeCallback);
		}
	}

	/**
	 * On change in "More than from now" selector
	 */
	onChangeSelector(selectedUnitOfTime: DurationConstructor) {
		this.setState({selectedUnitOfTime}, this.onChangeCallback);
	}

	/**
	 * On any change callback
	 */
	onChangeCallback() {
		const timeValue = this.state.inputStartValue;
		const unitOfTime = this.state.selectedUnitOfTime;
		if (timeValue && unitOfTime) {
			this.props.onChange(option(new MoreThanFromNowFilterModel(Number(timeValue), unitOfTime)));
		}
		else {
			this.props.onChange(none);
		}
	}

	/**
	 * Reset filter
	 */
	resetFilter() {
		this.setState({inputStartValue: ""});
	}
}

export class MoreThanFromNowFilterModel extends DateFilterModel {
	constructor(public readonly timeValue: number,
		public readonly unitOfTime: DurationConstructor) {
		super(DateFilterType.MoreThanFromNow)
	}

	static fromJson(json: DateFilterValueItemApiDto[]) {
		return new MoreThanFromNowFilterModel(json[0].value, json[0].rangeUnit.toLowerCase() as DurationConstructor)
	}

	getDisplayValue(): string {
		return `${super.getDisplayValue()} ${this.timeValue} ${this.unitOfTime} from now`;
	}

	toQueryJson(): DateFilterValueItemApiDto[] {
		return [{value: this.timeValue, rangeUnit: this.unitOfTime.toUpperCase()}]
	}
}
